import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import PropTypes from "prop-types";
import { useState } from "react";
import ReportExportButtons from "../../../Report/components/ReportExportButtons";
import "../../../Report/reportPageLayout.scss";
import "../../../Report/ChangeOfAttributesStatusReport.scss";
import ComplaintsHierarchyFilters from "./ComplaintsHierarchyFilters";
import "./ComplaintsReportShell.scss";

/**
 * Same layout shell as New Connection Posted for Proof Listing Report.
 * Table stays in children (BasicTable).
 */
export default function ComplaintsReportShell({
  pageTitle,
  bannerText,
  isExportEmpty,
  onEmpty,
  onPdf,
  onExcel,
  onPrint,
  filterProps,
  exportStripStart,
  belowBanner,
  children,
}) {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const renderFilters = ({ showGo = false } = {}) => {
    const FilterComponent = filterProps?.FilterComponent || ComplaintsHierarchyFilters;
    const { FilterComponent: _fc, ...rest } = filterProps || {};
    return <FilterComponent {...rest} showGo={showGo} />;
  };

  return (
    <Box className="form-box rpt-page coa-status-report pc-complaints-report-page">
      <div className="coa-layout">
        <header className="coa-page-header">
          <h1 className="coa-page-title">{pageTitle}</h1>
        </header>

        <div className="coa-toolbar-stack">
          <Paper
            variant="outlined"
            className="coa-card coa-card--filters"
            sx={{
              p: 2,
              width: "100%",
              bgcolor: "#fff",
              borderRadius: 2,
              borderColor: "divider",
            }}
          >
            <div
              className={
                isMdUp ? "coa-filters coa-filters--mui-grid" : "coa-filters"
              }
            >
              {isMdUp ? (
                renderFilters({ showGo: true })
              ) : (
                <p className="coa-filters-hint">
                  Tap <strong>Filters</strong> to set dates and office, then tap{" "}
                  <strong>GO</strong>.
                </p>
              )}
            </div>

            <Stack
              direction="row"
              flexWrap="wrap"
              alignItems="center"
              justifyContent={{ xs: "flex-start", md: "flex-end" }}
              spacing={1}
              className="coa-export-strip coa-export-strip--complaints"
              sx={{
                pt: 2,
                mt: 2,
                borderTop: "1px solid",
                borderColor: "divider",
                gap: 1,
                width: "100%",
              }}
            >
              {exportStripStart ? (
                <Box
                  className="pc-export-strip-start"
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 1,
                    minWidth: 0,
                    mr: { xs: 0, md: "auto" },
                  }}
                >
                  {exportStripStart}
                </Box>
              ) : null}
              <Stack
                direction="row"
                flexWrap="wrap"
                alignItems="center"
                justifyContent="flex-end"
                spacing={1}
                className="pc-export-strip-actions"
                sx={{ flex: "0 0 auto", gap: 1, ml: { xs: 0, md: "auto" } }}
              >
                {!isMdUp && (
                  <Tooltip title="Filters">
                    <IconButton
                      color="primary"
                      onClick={() => setMobileFilterOpen(true)}
                      aria-label="Open filters"
                      size="small"
                    >
                      <FilterListIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {!isMdUp && (
                  <Button
                    variant="contained"
                    disableElevation
                    className="coa-go-btn"
                    size="small"
                    onClick={filterProps?.onGoClick}
                    disabled={
                      filterProps?.isGoDisabled || filterProps?.viewBackdrop
                    }
                  >
                    GO
                  </Button>
                )}
                <ReportExportButtons
                  isEmpty={isExportEmpty}
                  onEmpty={onEmpty}
                  onPdf={onPdf}
                  onExcel={onExcel}
                  onPrint={onPrint}
                />
              </Stack>
            </Stack>
          </Paper>
        </div>

        {bannerText ? (
          <div className="coa-report-banner" role="status">
            {bannerText}
          </div>
        ) : null}

        {belowBanner ? (
          <div className="pc-complaints-below-banner">{belowBanner}</div>
        ) : null}

        <Box
          className="application-table pc-complaints-table-panel"
          sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}
        >
          {children}
        </Box>

        <Drawer
          anchor="left"
          open={mobileFilterOpen}
          onClose={() => setMobileFilterOpen(false)}
          PaperProps={{
            className: "coa-filter-drawer",
            sx: {
              width: { xs: "100%", sm: 400 },
              maxWidth: "100vw",
              boxSizing: "border-box",
            },
          }}
        >
          <Box
            className="coa-drawer-inner"
            sx={{ p: 2, pb: 3, boxSizing: "border-box" }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Typography variant="h6" component="div">
                Filters
              </Typography>
              <IconButton
                aria-label="Close filters"
                onClick={() => setMobileFilterOpen(false)}
                edge="end"
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Stack>
            {renderFilters({ showGo: true })}
          </Box>
        </Drawer>
      </div>
    </Box>
  );
}

ComplaintsReportShell.propTypes = {
  pageTitle: PropTypes.string.isRequired,
  bannerText: PropTypes.node,
  isExportEmpty: PropTypes.bool,
  onEmpty: PropTypes.func,
  onPdf: PropTypes.func,
  onExcel: PropTypes.func,
  onPrint: PropTypes.func,
  filterProps: PropTypes.object.isRequired,
  exportStripStart: PropTypes.node,
  belowBanner: PropTypes.node,
  children: PropTypes.node.isRequired,
};
